import { Component, createRef } from 'preact';

import classnames from 'classnames';
import { subscribe } from 'infinite-ui-event';
import { findDOMNode } from 'preact/compat';

import pkg from '../../../package.json';
import { LessGlobals } from '../../constants/lessGlobals.cjs';
import { error } from '../../lib/Log';
import { isDesktop, isMobile } from '../../lib/Media';
import ViewContext from '../../view/ViewContext';
import { Badge } from '../badge/Badge';
import Button from '../button/Button';
import Control from '../control/Control';
import Icon from '../icon/Icon';
import Menu, { MENU_QUERY_PARAMETER } from '../menu/Menu';
import Portal from '../portal/Portal';
import Search, { SEARCH_QUERY_PARAMETER } from '../search/Search';
import Service from '../service/Service';
import Seznam from '../seznam/Seznam';

// import 'preact/devtools';

const KEY_CODE_ESC = 27;
const SCROLL_DEAD_ZONE = 28;

export default class Ribbon extends Component {
	static get contextType() {
		return ViewContext;
	}

	get searchEnabled() {
		return this.context.props.search !== false && (!this.state.unfixed || isDesktop());
	}

	constructor(props, context) {
		super(props, context);

		this._searchRef = createRef();
		this._menuButtonRef = createRef();

		this._resizeSubscription = null;
		this._scrollSubscription = null;

		this._lastScrollTop = 0;

		this.state = {
			showSearch: false,
			unfixed: !!(props.unfixed || context.props.unfixed)
		};
	}

	componentDidMount() {
		if (!isMobile() || window.location.search.includes(SEARCH_QUERY_PARAMETER)) {
			this._showSearch();
		}

		if (window.location.search.includes(MENU_QUERY_PARAMETER)) {
			setTimeout(() => this._toggleMenu(), 1); // merkur component is not fully mounted, we have to delay toggle
		}

		this._scrollSubscription = subscribe('scroll', payload => this.onScroll(payload));

		this._resizeSubscription = subscribe('resize', payload => this.onResize(payload));

		window.addEventListener('keydown', this);
	}

	componentDidUpdate(prevProps) {
		const { showMenu, unfixed } = this.props;

		if (showMenu) {
			document.body.scrollTop = 0; // For Safari
			document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
			this._lastScrollTop = 0;
		}

		if (prevProps.unfixed !== unfixed) {
			this.setState({ unfixed });
		}

		if (prevProps.showMenu && !showMenu && this._menuButtonRef.current) {
			// eslint-disable-next-line react/no-find-dom-node
			findDOMNode(this._menuButtonRef.current)?.firstChild?.focus?.();
		}
	}

	componentWillUnmount() {
		this._resizeSubscription.unsubscribe();
		this._scrollSubscription.unsubscribe();

		window.removeEventListener('keydown', this);
	}

	render() {
		const { menu, search, service } = this.context.props;

		if (!service || !service.id) {
			error('Service ID is required property.');

			return null;
		}

		const { searchQuery, showMenu } = this.props;
		const { showSearch, unfixed } = this.state;
		const { searchEnabled } = this;
		const height = Number.parseInt(LessGlobals.height);

		return (
			<header
				className={classnames({
					ribbon: true,
					'ribbon--base': true,
					'ribbon--unfixed': unfixed && !showMenu,
					'ribbon--show-search': showSearch
				})}
				aria-label='Hlavička'
				data-ribbon={height} // Backwards compatibility
				data-ribbon-height={height}
				data-ribbon-version={pkg.version}
				role='banner'
			>
				<div className='ribbon__start'>
					{menu && (!!menu.length || typeof menu === 'boolean') && (
						<Button
							className='ribbon__menu-button'
							action={MENU_QUERY_PARAMETER}
							onClick={() => this._toggleMenu()}
							role='navigation'
							aria-label={showMenu ? 'Zavřít hlavní menu' : 'Otevřít hlavní menu'}
							data-dot={`lista_hamburger${showMenu ? '_zavrit' : ''}`}
							ref={this._menuButtonRef}
						>
							<Icon icon={showMenu ? 'cross' : 'menu'} />
						</Button>
					)}
					<Service />
				</div>
				<Portal className='ribbon__center' shouldComponentUpdate={() => search !== false}>
					{searchEnabled && (
						<Search
							hidden={!showSearch}
							ref={this._searchRef}
							searchQuery={searchQuery}
						/>
					)}
				</Portal>
				<div className='ribbon__end'>
					<Control />
					<nav
						className='ribbon__end--reversed'
						role='navigation'
						aria-labelledby='ribbon-os-menu'
					>
						<h6 className='ribbon--blind' id='ribbon-os-menu'>
							Osobní menu
						</h6>
						<Seznam />
						<Badge />
					</nav>
				</div>
				<Menu hidden={!showMenu} onClose={() => this._toggleMenu()} />
			</header>
		);
	}

	handleEvent(event) {
		if (this.props.showMenu && event.keyCode === KEY_CODE_ESC) {
			this._toggleMenu();
		}
	}

	onResize(payload) {
		if (payload.resize.delta.width === 0) {
			return;
		}

		if (isDesktop()) {
			this._showSearch();
		} else {
			this._hideSearch();
		}
	}

	onScroll(payload) {
		if (!this.searchEnabled) {
			return;
		}

		const { delta, top } = payload.scroll;
		const { showSearch } = this.state;

		if (!isMobile() || Math.abs(top - this._lastScrollTop) < SCROLL_DEAD_ZONE) {
			return;
		}

		this._lastScrollTop = top;

		if (delta > 0 && showSearch === false) {
			this._showSearch();
		}

		const { current: searchElm } = this._searchRef;

		if (delta < 0 && showSearch === true && !searchElm.isFocused) {
			this._hideSearch();
		}
	}

	_hideSearch() {
		if (this.state.showSearch) {
			this.setState({
				showSearch: false
			});
		}
	}

	_showSearch() {
		if (!this.state.showSearch) {
			this.setState({
				showSearch: true
			});
		}
	}

	_toggleMenu() {
		const { showMenu } = this.props;
		this.context.toggleMenu();
		this.context.emit('menu.toggle', { open: !showMenu });
	}
}
